import { getSessionHeaders } from './serverSession';

// KYC is required for ALL withdrawals — no threshold exceptions (v2.9.74)

export interface KYCValidationResult {
 isValid: boolean;
 status: 'not_started' | 'pending' | 'approved' | 'rejected' | 'expired';
 message: string;
 canWithdraw: boolean;
 canReceiveDividends: boolean;
 totalWithdrawn?: number;
 remainingWithoutKYC?: number;
 requiresKYC?: boolean;
}

/**
 * Get total amount user has withdrawn (to check against KYC threshold)
 * Uses API endpoint - NO getServiceRoleClient() in browser
 */
const __getTotalWithdrawn = async (userId: number): Promise<number> => {
 try {
 const response = await fetch('/api/user/kyc-validation', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ userId, action: 'getTotalWithdrawn' })
 });

 if (!response.ok) {
 console.error('Error fetching withdrawal history');
 return 0;
 }

 const data = await response.json();
 console.log(`User ${userId} has withdrawn total: $${data.total.toFixed(2)}`);
 return data.total;
 } catch (error) {
 console.error('Error calculating total withdrawn:', error);
 return 0;
 }
};

/**
 * Validate if user has completed KYC for financial operations
 * KYC must be approved before any withdrawal is allowed (v2.9.74)
 * Uses API endpoint - NO getServiceRoleClient() in browser
 */
export const validateKYCForFinancialOperations = async (userId: number): Promise<KYCValidationResult> => {
 try {
 console.log(`Validating KYC for user ${userId} for financial operations...`);

 const response = await fetch('/api/user/kyc-validation', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ userId })
 });

 if (!response.ok) {
 console.error('API error validating KYC');
 return {
 isValid: false,
 status: 'not_started',
 message: 'Error validating KYC status. Please try again.',
 canWithdraw: false,
 canReceiveDividends: false
 };
 }

 const result = await response.json();

 console.log(`KYC Status Check:
 - Status: ${result.status}
 - Can Withdraw: ${result.canWithdraw}
 - Total Withdrawn: $${result.totalWithdrawn?.toFixed(2) || '0.00'}`);

 return result;

 } catch (error) {
 console.error('KYC validation error:', error);
 return {
 isValid: false,
 status: 'not_started',
 message: 'Error validating KYC status. Please try again.',
 canWithdraw: false,
 canReceiveDividends: false
 };
 }
};

/**
 * Get user-friendly KYC status message
 */
export const getKYCStatusMessage = (status: string): string => {
 switch (status) {
 case 'not_started':
 return ' Complete KYC verification to unlock commission withdrawals and dividend payments.';
 case 'pending':
 case 'pending_review':
 return '⏳ Your KYC is under review. You\'ll be able to withdraw once approved.';
 case 'approved':
 return ' KYC verified! You can withdraw commissions and receive dividends.';
 case 'rejected':
 return ' KYC rejected. Please update your information and resubmit.';
 case 'expired':
 return ' KYC expired. Please update your verification to continue withdrawing.';
 default:
 return ' KYC verification required for financial operations.';
 }
};

/**
 * Check if user can perform specific financial operation
 */
export const canPerformFinancialOperation = async (
 userId: number, 
 operation: 'withdraw_commission' | 'receive_dividend'
): Promise<{ allowed: boolean; message: string }> => {
 const validation = await validateKYCForFinancialOperations(userId);
 
 if (operation === 'withdraw_commission') {
 return {
 allowed: validation.canWithdraw,
 message: validation.canWithdraw 
 ? 'You can withdraw your commissions.' 
 : validation.message
 };
 }
 
 if (operation === 'receive_dividend') {
 return {
 allowed: validation.canReceiveDividends,
 message: validation.canReceiveDividends 
 ? 'You can receive dividend payments.' 
 : validation.message
 };
 }
 
 return {
 allowed: false,
 message: 'Unknown operation type.'
 };
};
